'use client';

/**
 * Internal SmartTask offline queue/storage primitives.
 *
 * App-level feature code should prefer the shared `masterSync` facade for
 * queue reads, history access, cached workflow snapshots, and sync triggers.
 * This module remains the lower-level implementation for queue persistence,
 * replay, and optimistic-state helpers.
 */

import appShellStore from '@/lib/offline/appShellStore';
import offlineDb, { type PublishRetryRecord } from '@/lib/offline/offlineDb';
import { getApiUrl } from '@/lib/apiConfig';
import { withOperationsDemoFetchInit } from '@/lib/demo/clientContext';
import { appendOfflineMutation, updateOfflineMutation } from '@/lib/offlineMutationLedger';
import {
  enrichVehicleInspectionActionPayload,
  stripVehicleInspectionOfflineSource,
  type VehicleInspectionActionPayload,
} from '@/lib/operations/smartTaskVehicleInspectionPayload';
import {
  getWorkItemCommentTypeForPersistence,
  getWorkItemCommentTypeLabel,
  type WorkItemCommentType,
} from '@/lib/work-items/commentTypes';
import type { WorkflowAction } from '@/app/client-zone/modules/total-operations/components/smart-task/engine/workflowEngine';
import type {
  GPSLocation,
  WorkItem,
  WorkflowState,
  WorkflowStateRecord,
} from '@/app/client-zone/modules/total-operations/components/smart-task/types';

const CACHE_VERSION = 1;
const MAX_HISTORY_ENTRIES = 50;
const MAX_SYNC_ATTEMPTS = 5;

const PENDING_ACTIONS_PREFIX = `smarttask_pending_actions:v${CACHE_VERSION}:user:`;
const ACTION_HISTORY_PREFIX = `smarttask_action_history:v${CACHE_VERSION}:user:`;
const WORKFLOW_SNAPSHOT_PREFIX = `smarttask_workflow_snapshot:v${CACHE_VERSION}:user:`;
const SMARTTASK_MUTATION_PREFIX = 'smarttask_action';

const ACTION_LABELS: Record<string, string> = {
  start_travel: 'Start Travel',
  start_trip: 'Start Digital Logbook Trip',
  arrive_site: 'Arrive On Site',
  check_in: 'Check In',
  check_in_without_travel: 'Check In',
  pause_work: 'Pause Work',
  resume_work: 'Resume Work',
  start_completing: 'Start Completing',
  complete_job: 'Complete Job',
  reset_workflow: 'Release Workflow',
  work_item_status_update: 'Update Work Item Status',
  set_vehicle: 'Assign Vehicle',
  release_vehicle: 'Release Vehicle',
  record_inspection: 'Record Inspection',
  cancel_inspection: 'Cancel Inspection',
  override_inspection: 'Override Inspection',
  consume_inspection_override: 'Consume Inspection Override',
  updateVehicleDetails: 'Update Vehicle Details',
  updateCurrentDriver: 'Update Vehicle Driver',
  time_entry: 'Log Time Entry',
  follow_up: 'Schedule Follow-up',
  site_assessment: 'Submit Site Assessment',
  route_stop_update: 'Route Stop Update',
  comment_create: 'Add Comment',
};

export type SmartTaskVehicleAction = 'set_vehicle' | 'release_vehicle' | 'record_inspection' | 'cancel_inspection' | 'override_inspection' | 'consume_inspection_override';
export type SmartTaskFleetAction = 'updateVehicleDetails' | 'updateCurrentDriver';
export type SmartTaskFieldServiceAction = 'time_entry' | 'follow_up' | 'site_assessment';
export type SmartTaskLogbookAction = 'start_trip';

export interface SmartTaskOfflineActivityContext {
  item_type?: string | null;
  work_item_number?: string | null;
  group_id?: string | null;
  group_number?: string | null;
  parent_job_id?: string | null;
}

export type SmartTaskOfflineActionKind = 'workflow_transition' | 'workflow_reset' | 'work_item_status_update' | 'vehicle_action' | 'fleet_action' | 'field_service_action' | 'logbook_action' | 'route_stop_update' | 'comment_create';

interface SmartTaskOfflineActionBase {
  local_action_id: string;
  offline_action_id: string;
  kind: SmartTaskOfflineActionKind;
  client_id: number;
  user_id: number;
  solution_key: string | null;
  work_item_id: string | null;
  created_at: string;
  updated_at: string;
  attempts: number;
  last_error?: string;
  timeline_label: string;
  activity_context?: SmartTaskOfflineActivityContext | null;
}

export interface SmartTaskQueuedWorkflowTransition extends SmartTaskOfflineActionBase {
  kind: 'workflow_transition';
  action: WorkflowAction;
  expected_from_state: WorkflowState | null;
  to_state: WorkflowState;
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedWorkflowReset extends SmartTaskOfflineActionBase {
  kind: 'workflow_reset';
  action: 'reset_workflow';
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedWorkItemStatusUpdate extends SmartTaskOfflineActionBase {
  kind: 'work_item_status_update';
  action: 'work_item_status_update';
  target_status: string;
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedVehicleAction extends SmartTaskOfflineActionBase {
  kind: 'vehicle_action';
  action: SmartTaskVehicleAction;
  vehicle_id?: string | null;
  vehicle_registration?: string | null;
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedFleetAction extends SmartTaskOfflineActionBase {
  kind: 'fleet_action';
  action: SmartTaskFleetAction;
  vehicle_id?: string | null;
  vehicle_registration?: string | null;
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedFieldServiceAction extends SmartTaskOfflineActionBase {
  kind: 'field_service_action';
  action: SmartTaskFieldServiceAction;
  work_item_id: string;
  endpoint: string;
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedLogbookAction extends SmartTaskOfflineActionBase {
  kind: 'logbook_action';
  action: SmartTaskLogbookAction;
  endpoint: string;
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedRouteStopUpdate extends SmartTaskOfflineActionBase {
  kind: 'route_stop_update';
  action: 'route_stop_update';
  route_id: string;
  stop_work_item_id: string;
  stop_status: 'arrived' | 'departed' | 'completed' | 'skipped' | 'failed';
  payload: Record<string, unknown>;
}

export interface SmartTaskQueuedCommentCreate extends SmartTaskOfflineActionBase {
  kind: 'comment_create';
  action: 'comment_create';
  work_item_id: string;
  endpoint: string;
  comment_type: WorkItemCommentType;
  payload: {
    comment_text: string;
    is_internal?: boolean;
    comment_type?: WorkItemCommentType;
    is_itemized?: boolean;
    mentioned_user_ids?: number[];
    parent_comment_id?: string | null;
    activity_metadata?: Record<string, unknown>;
  };
}

export type SmartTaskQueuedAction = SmartTaskQueuedWorkflowTransition | SmartTaskQueuedWorkflowReset | SmartTaskQueuedWorkItemStatusUpdate | SmartTaskQueuedVehicleAction | SmartTaskQueuedFleetAction | SmartTaskQueuedFieldServiceAction | SmartTaskQueuedLogbookAction | SmartTaskQueuedRouteStopUpdate | SmartTaskQueuedCommentCreate;

export interface SmartTaskOfflineActionHistoryEntry {
  local_action_id: string;
  offline_action_id: string;
  kind: SmartTaskOfflineActionKind;
  action: string;
  status: 'queued' | 'synced' | 'failed';
  timeline_label: string;
  work_item_id: string | null;
  created_at: string;
  synced_at?: string;
  error?: string;
  activity_context?: SmartTaskOfflineActivityContext | null;
}

function getSmartTaskMutationDedupeKey(offlineActionId: string): string {
  return `${SMARTTASK_MUTATION_PREFIX}:${offlineActionId}`;
}

function normalizeSolutionKey(solutionKey?: string | null): string | null {
  return typeof solutionKey === 'string' && solutionKey.trim().length > 0
    ? solutionKey.trim()
    : null;
}

function hasValidIdentity(clientId: number, userId: number): boolean {
  return Number.isFinite(clientId) && clientId > 0 && Number.isFinite(userId) && userId > 0;
}

function getPendingActionsKey(userId: number): string {
  return `${PENDING_ACTIONS_PREFIX}${userId}`;
}

function getActionHistoryKey(userId: number): string {
  return `${ACTION_HISTORY_PREFIX}${userId}`;
}

function getWorkflowSnapshotKey(userId: number): string {
  return `${WORKFLOW_SNAPSHOT_PREFIX}${userId}`;
}

function generateLocalActionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `smarttask_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function buildTimelineLabel(action: string, customLabel?: string): string {
  return customLabel || ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function getSmartTaskFieldServiceEndpoint(action: SmartTaskFieldServiceAction, workItemId: string): string {
  const normalizedWorkItemId = String(workItemId || '').trim();
  if (!normalizedWorkItemId) {
    throw new Error('Work item ID is required for SmartTask field-service actions');
  }

  switch (action) {
    case 'time_entry':
      return `/api/v1/work-items/${normalizedWorkItemId}/time-entries`;
    case 'follow_up':
      return `/api/v1/work-items/${normalizedWorkItemId}/follow-up`;
    case 'site_assessment':
      return `/api/v1/work-items/${normalizedWorkItemId}/site-assessment`;
    default:
      return `/api/v1/work-items/${normalizedWorkItemId}`;
  }
}

export function getSmartTaskLogbookEndpoint(action: SmartTaskLogbookAction): string {
  switch (action) {
    case 'start_trip':
    default:
      return '/api/v1/operations/logbook';
  }
}

const SMARTTASK_QUEUE_DOMAIN = 'app-shell' as const;
const SMARTTASK_ENTITY_TYPE_PREFIX = 'smarttask.';

function getPublishQueueEntityType(kind: SmartTaskOfflineActionKind): string {
  return `${SMARTTASK_ENTITY_TYPE_PREFIX}${kind}`;
}

function getPublishQueueEntityId(userId: number, offlineActionId: string): string {
  return `u${userId}:${offlineActionId}`;
}

function computeSmartTaskRetryBackoff(attempt: number): string {
  const baseSeconds = 30;
  const maxSeconds = 30 * 60;
  const delay = Math.min(baseSeconds * Math.pow(2, attempt - 1), maxSeconds);
  return new Date(Date.now() + delay * 1000).toISOString();
}

function publishRowToEntry(row: PublishRetryRecord): SmartTaskQueuedAction | null {
  try {
    const entry = JSON.parse(row.payload) as SmartTaskQueuedAction;
    if (!entry || typeof entry !== 'object' || !entry.kind) return null;
    return {
      ...entry,
      attempts: row.attempts,
      updated_at: row.updated_at,
      last_error: row.error_message ?? entry.last_error,
    };
  } catch {
    return null;
  }
}

async function enqueuePendingAction(clientId: number, userId: number, entry: SmartTaskQueuedAction): Promise<void> {
  if (!offlineDb || !hasValidIdentity(clientId, userId)) return;
  await offlineDb.publishQueue.add({
    domain: SMARTTASK_QUEUE_DOMAIN,
    client_id: clientId,
    operation: entry.action,
    entity_type: getPublishQueueEntityType(entry.kind),
    entity_id: getPublishQueueEntityId(userId, entry.offline_action_id),
    status: 'pending',
    attempts: entry.attempts || 0,
    max_attempts: MAX_SYNC_ATTEMPTS,
    created_at: entry.created_at,
    updated_at: entry.updated_at,
    next_retry_at: entry.created_at,
    error_message: entry.last_error,
    payload: JSON.stringify(entry),
  });
}

async function migrateLegacyPendingQueue(clientId: number, userId: number): Promise<void> {
  if (!offlineDb) return;
  const legacy = await appShellStore.loadSettingsArrayMigration<SmartTaskQueuedAction>(
    clientId,
    getPendingActionsKey(userId),
  );
  if (legacy.length === 0) return;

  for (const entry of legacy) {
    try {
      const attempts = entry.attempts || 0;
      const isDead = attempts >= MAX_SYNC_ATTEMPTS;
      await offlineDb.publishQueue.add({
        domain: SMARTTASK_QUEUE_DOMAIN,
        client_id: clientId,
        operation: entry.action,
        entity_type: getPublishQueueEntityType(entry.kind),
        entity_id: getPublishQueueEntityId(userId, entry.offline_action_id),
        status: isDead ? 'dead_letter' : attempts > 0 ? 'failed' : 'pending',
        attempts,
        max_attempts: MAX_SYNC_ATTEMPTS,
        created_at: entry.created_at,
        updated_at: entry.updated_at,
        last_attempt: attempts > 0 ? entry.updated_at : undefined,
        next_retry_at: isDead ? undefined : attempts > 0 ? computeSmartTaskRetryBackoff(attempts) : entry.created_at,
        error_message: entry.last_error,
        payload: JSON.stringify(entry),
      });
    } catch (error) {
      console.warn('[SmartTask] Failed to migrate legacy pending action:', error);
    }
  }

  await appShellStore.deleteJson(clientId, getPendingActionsKey(userId));
}

async function loadPendingQueueRows(clientId: number, userId: number): Promise<PublishRetryRecord[]> {
  if (!offlineDb || !hasValidIdentity(clientId, userId)) return [];
  await migrateLegacyPendingQueue(clientId, userId);
  const entityIdPrefix = `u${userId}:`;
  const rows = await offlineDb.publishQueue
    .where('[domain+client_id]')
    .equals([SMARTTASK_QUEUE_DOMAIN, clientId])
    .and((row) => (
      row.entity_type.startsWith(SMARTTASK_ENTITY_TYPE_PREFIX)
      && row.entity_id.startsWith(entityIdPrefix)
      && row.status !== 'completed'
      && row.status !== 'dead_letter'
    ))
    .toArray();

  return rows.sort((a, b) => {
    const byCreatedAt = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return byCreatedAt !== 0 ? byCreatedAt : (a.id ?? 0) - (b.id ?? 0);
  });
}

async function loadActionHistoryStore(clientId: number, userId: number): Promise<SmartTaskOfflineActionHistoryEntry[]> {
  if (!hasValidIdentity(clientId, userId)) return [];
  return appShellStore.loadSettingsArrayMigration<SmartTaskOfflineActionHistoryEntry>(
    clientId,
    getActionHistoryKey(userId),
  );
}

async function saveActionHistoryStore(clientId: number, userId: number, entries: SmartTaskOfflineActionHistoryEntry[]): Promise<void> {
  if (!hasValidIdentity(clientId, userId)) return;
  await appShellStore.saveSettingsArrayMigration(
    clientId,
    getActionHistoryKey(userId),
    entries.slice(0, MAX_HISTORY_ENTRIES),
  );
}

async function upsertActionHistoryEntry(clientId: number, userId: number, entry: SmartTaskOfflineActionHistoryEntry): Promise<void> {
  const history = await loadActionHistoryStore(clientId, userId);
  const nextHistory = [entry, ...history.filter((existing) => existing.local_action_id !== entry.local_action_id)];
  await saveActionHistoryStore(clientId, userId, nextHistory);
}

export async function loadPendingSmartTaskActions(clientId: number, userId: number): Promise<SmartTaskQueuedAction[]> {
  const rows = await loadPendingQueueRows(clientId, userId);
  return rows
    .map(publishRowToEntry)
    .filter((entry): entry is SmartTaskQueuedAction => entry !== null);
}

export async function getPendingSmartTaskActionCount(clientId: number, userId: number): Promise<number> {
  const rows = await loadPendingQueueRows(clientId, userId);
  return rows.length;
}

/**
 * Internal storage accessor. App/UI consumers should use the `masterSync`
 * wrappers instead of importing this directly.
 */
export async function loadSmartTaskOfflineActionHistory(
  clientId: number,
  userId: number,
  limit = 10,
): Promise<SmartTaskOfflineActionHistoryEntry[]> {
  const history = await loadActionHistoryStore(clientId, userId);
  return history.slice(0, Math.max(0, limit));
}

export async function queueSmartTaskOfflineTransition(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  action: WorkflowAction;
  expectedFromState: WorkflowState | null;
  toState: WorkflowState;
  workItemId?: string | null;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedWorkflowTransition> {
  const { clientId, userId, solutionKey, action, expectedFromState, toState, workItemId, payload, timelineLabel, activityContext } = params;
  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const now = new Date().toISOString();
  const localActionId = generateLocalActionId();
  const offlineActionId = generateLocalActionId();
  const mergedPayload: Record<string, unknown> = {
    ...payload,
    metadata: {
      ...((payload.metadata as Record<string, unknown>) || {}),
      offline_action_id: offlineActionId,
      offline_queued_at: now,
      queued_via: 'smarttask_offline_action_queue',
    },
  };

  const entry: SmartTaskQueuedWorkflowTransition = {
    local_action_id: localActionId,
    offline_action_id: offlineActionId,
    kind: 'workflow_transition',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: workItemId ? String(workItemId) : null,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel(action, timelineLabel),
    activity_context: activityContext || null,
    action,
    expected_from_state: expectedFromState,
    to_state: toState,
    payload: mergedPayload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.work_item_id || entry.local_action_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineWorkflowReset(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  workItemId?: string | null;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedWorkflowReset> {
  const { clientId, userId, solutionKey, workItemId, payload, timelineLabel, activityContext } = params;
  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const now = new Date().toISOString();
  const entry: SmartTaskQueuedWorkflowReset = {
    local_action_id: generateLocalActionId(),
    offline_action_id: generateLocalActionId(),
    kind: 'workflow_reset',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: workItemId ? String(workItemId) : null,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel('reset_workflow', timelineLabel),
    activity_context: activityContext || null,
    action: 'reset_workflow',
    payload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.work_item_id || entry.local_action_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineStatusUpdate(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  workItemId: string;
  status: string;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedWorkItemStatusUpdate> {
  const { clientId, userId, solutionKey, workItemId, status, payload, timelineLabel, activityContext } = params;
  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const normalizedWorkItemId = String(workItemId || '').trim();
  if (!normalizedWorkItemId) {
    throw new Error('Work item ID is required for offline status updates');
  }

  const now = new Date().toISOString();
  const entry: SmartTaskQueuedWorkItemStatusUpdate = {
    local_action_id: generateLocalActionId(),
    offline_action_id: generateLocalActionId(),
    kind: 'work_item_status_update',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: normalizedWorkItemId,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel('work_item_status_update', timelineLabel || `Set status to ${status}`),
    activity_context: activityContext || null,
    action: 'work_item_status_update',
    target_status: status,
    payload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.work_item_id || entry.local_action_id,
    operationType: entry.target_status,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      target_status: entry.target_status,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineVehicleAction(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  action: SmartTaskVehicleAction;
  workItemId?: string | null;
  vehicleId?: string | null;
  vehicleRegistration?: string | null;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedVehicleAction> {
  const {
    clientId,
    userId,
    solutionKey,
    action,
    workItemId,
    vehicleId,
    vehicleRegistration,
    payload,
    timelineLabel,
    activityContext,
  } = params;

  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const now = new Date().toISOString();
  const entry: SmartTaskQueuedVehicleAction = {
    local_action_id: generateLocalActionId(),
    offline_action_id: generateLocalActionId(),
    kind: 'vehicle_action',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: workItemId ? String(workItemId) : null,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel(action, timelineLabel),
    activity_context: activityContext || null,
    action,
    vehicle_id: vehicleId ? String(vehicleId) : null,
    vehicle_registration: vehicleRegistration || null,
    payload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.vehicle_id || entry.work_item_id || entry.local_action_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      vehicle_id: entry.vehicle_id,
      vehicle_registration: entry.vehicle_registration,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineFleetAction(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  action: SmartTaskFleetAction;
  workItemId?: string | null;
  vehicleId?: string | null;
  vehicleRegistration?: string | null;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedFleetAction> {
  const {
    clientId,
    userId,
    solutionKey,
    action,
    workItemId,
    vehicleId,
    vehicleRegistration,
    payload,
    timelineLabel,
    activityContext,
  } = params;

  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const now = new Date().toISOString();
  const entry: SmartTaskQueuedFleetAction = {
    local_action_id: generateLocalActionId(),
    offline_action_id: generateLocalActionId(),
    kind: 'fleet_action',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: workItemId ? String(workItemId) : null,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel(action, timelineLabel),
    activity_context: activityContext || null,
    action,
    vehicle_id: vehicleId ? String(vehicleId) : null,
    vehicle_registration: vehicleRegistration || null,
    payload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.vehicle_id || entry.work_item_id || entry.local_action_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      vehicle_id: entry.vehicle_id,
      vehicle_registration: entry.vehicle_registration,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineFieldServiceAction(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  action: SmartTaskFieldServiceAction;
  workItemId: string;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedFieldServiceAction> {
  const {
    clientId,
    userId,
    solutionKey,
    action,
    workItemId,
    payload,
    timelineLabel,
    activityContext,
  } = params;

  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const normalizedWorkItemId = String(workItemId || '').trim();
  if (!normalizedWorkItemId) {
    throw new Error('Work item ID is required for SmartTask field-service actions');
  }

  const now = new Date().toISOString();
  const entry: SmartTaskQueuedFieldServiceAction = {
    local_action_id: generateLocalActionId(),
    offline_action_id: generateLocalActionId(),
    kind: 'field_service_action',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: normalizedWorkItemId,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel(action, timelineLabel),
    activity_context: activityContext || null,
    action,
    endpoint: getSmartTaskFieldServiceEndpoint(action, normalizedWorkItemId),
    payload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.work_item_id || entry.local_action_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      endpoint: entry.endpoint,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineLogbookAction(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  action: SmartTaskLogbookAction;
  workItemId?: string | null;
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedLogbookAction> {
  const {
    clientId,
    userId,
    solutionKey,
    action,
    workItemId,
    payload,
    timelineLabel,
    activityContext,
  } = params;

  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const now = new Date().toISOString();
  const offlineActionId = generateLocalActionId();
  const mergedPayload: Record<string, unknown> = {
    ...payload,
    metadata: {
      ...((payload?.metadata as Record<string, unknown>) && typeof payload.metadata === 'object' ? payload.metadata : {}),
      offline_action_id: offlineActionId,
      offline_queued_at: now,
      queued_via: 'smarttask_offline_logbook_queue',
    },
  };

  const entry: SmartTaskQueuedLogbookAction = {
    local_action_id: generateLocalActionId(),
    offline_action_id: offlineActionId,
    kind: 'logbook_action',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: workItemId ? String(workItemId) : null,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel(action, timelineLabel),
    activity_context: activityContext || null,
    action,
    endpoint: getSmartTaskLogbookEndpoint(action),
    payload: mergedPayload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.work_item_id || entry.local_action_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      endpoint: entry.endpoint,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineRouteStopUpdate(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  routeId: string;
  workItemId?: string | null;
  stopWorkItemId: string;
  stopStatus: 'arrived' | 'departed' | 'completed' | 'skipped' | 'failed';
  payload: Record<string, unknown>;
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedRouteStopUpdate> {
  const { clientId, userId, solutionKey, routeId, workItemId, stopWorkItemId, stopStatus, payload, timelineLabel, activityContext } = params;
  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const now = new Date().toISOString();
  const entry: SmartTaskQueuedRouteStopUpdate = {
    local_action_id: generateLocalActionId(),
    offline_action_id: generateLocalActionId(),
    kind: 'route_stop_update',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: workItemId ? String(workItemId) : null,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel('route_stop_update', timelineLabel || `Route stop ${stopStatus}`),
    activity_context: activityContext || null,
    action: 'route_stop_update',
    route_id: routeId,
    stop_work_item_id: String(stopWorkItemId),
    stop_status: stopStatus,
    payload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.stop_work_item_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      route_id: entry.route_id,
      stop_status: entry.stop_status,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function queueSmartTaskOfflineCommentCreate(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  workItemId: string;
  payload: {
    comment_text: string;
    is_internal?: boolean;
    comment_type?: WorkItemCommentType;
    is_itemized?: boolean;
    mentioned_user_ids?: number[];
    parent_comment_id?: string | null;
    activity_metadata?: Record<string, unknown>;
  };
  timelineLabel?: string;
  activityContext?: SmartTaskOfflineActivityContext | null;
}): Promise<SmartTaskQueuedCommentCreate> {
  const { clientId, userId, solutionKey, workItemId, payload, timelineLabel, activityContext } = params;
  if (!hasValidIdentity(clientId, userId)) {
    throw new Error('Invalid SmartTask offline identity');
  }

  const normalizedWorkItemId = String(workItemId || '').trim();
  if (!normalizedWorkItemId) {
    throw new Error('Work item ID is required for offline comments');
  }

  const commentText = typeof payload.comment_text === 'string' ? payload.comment_text.trim() : '';
  if (!commentText) {
    throw new Error('Comment text is required for offline comments');
  }

  const now = new Date().toISOString();
  const offlineActionId = generateLocalActionId();
  const nextCommentType = getWorkItemCommentTypeForPersistence(payload.comment_type, payload.is_internal);
  const mergedPayload = {
    comment_text: commentText,
    is_internal: nextCommentType === 'internal',
    comment_type: nextCommentType,
    is_itemized: payload.is_itemized ?? true,
    ...(Array.isArray(payload.mentioned_user_ids) && payload.mentioned_user_ids.length > 0
      ? { mentioned_user_ids: payload.mentioned_user_ids }
      : {}),
    ...(payload.parent_comment_id ? { parent_comment_id: payload.parent_comment_id } : {}),
    activity_metadata: {
      ...((payload.activity_metadata as Record<string, unknown>) || {}),
      offline_action_id: offlineActionId,
      offline_queued_at: now,
      queued_via: 'smarttask_offline_comment_queue',
    },
  };

  const entry: SmartTaskQueuedCommentCreate = {
    local_action_id: generateLocalActionId(),
    offline_action_id: offlineActionId,
    kind: 'comment_create',
    client_id: clientId,
    user_id: userId,
    solution_key: normalizeSolutionKey(solutionKey),
    work_item_id: normalizedWorkItemId,
    created_at: now,
    updated_at: now,
    attempts: 0,
    timeline_label: buildTimelineLabel(
      'comment_create',
      timelineLabel || `${getWorkItemCommentTypeLabel(nextCommentType)} comment`,
    ),
    activity_context: activityContext || null,
    action: 'comment_create',
    endpoint: `/api/v1/work-items/${normalizedWorkItemId}/comments`,
    comment_type: nextCommentType,
    payload: mergedPayload,
  };

  await enqueuePendingAction(clientId, userId, entry);
  await upsertActionHistoryEntry(clientId, userId, {
    local_action_id: entry.local_action_id,
    offline_action_id: entry.offline_action_id,
    kind: entry.kind,
    action: entry.action,
    status: 'queued',
    timeline_label: entry.timeline_label,
    work_item_id: entry.work_item_id,
    created_at: entry.created_at,
    activity_context: entry.activity_context || null,
  });

  await appendOfflineMutation({
    clientId,
    userId,
    module: 'operations',
    entityType: entry.kind,
    entityId: entry.work_item_id,
    operationType: entry.action,
    scope: entry.activity_context?.group_id ? 'group' : 'self',
    dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
    payload: entry.payload,
    metadata: {
      local_action_id: entry.local_action_id,
      timeline_label: entry.timeline_label,
      solution_key: entry.solution_key,
      comment_type: entry.comment_type,
      activity_context: entry.activity_context || null,
    },
  });

  return entry;
}

export async function loadPendingSmartTaskComments(
  clientId: number,
  userId: number,
  workItemIds?: string[],
): Promise<SmartTaskQueuedCommentCreate[]> {
  const normalizedWorkItemIds = Array.isArray(workItemIds)
    ? new Set(workItemIds.map((value) => String(value || '').trim()).filter(Boolean))
    : null;

  const queue = await loadPendingSmartTaskActions(clientId, userId);
  return queue.filter((entry): entry is SmartTaskQueuedCommentCreate => {
    if (entry.kind !== 'comment_create') {
      return false;
    }

    if (!normalizedWorkItemIds || normalizedWorkItemIds.size === 0) {
      return true;
    }

    return normalizedWorkItemIds.has(String(entry.work_item_id || '').trim());
  });
}

export function createEmptyWorkflowStateRecord(clientId: number, userId: number): WorkflowStateRecord {
  return {
    id: 'offline-smarttask-workflow-state',
    client_id: clientId,
    user_id: userId,
    current_state: 'idle',
    active_work_item_id: null,
    traveling_to_work_item_id: null,
    current_vehicle_id: null,
    current_vehicle_registration: null,
    state_started_at: new Date().toISOString(),
    travel_started_at: null,
    travel_ended_at: null,
    work_started_at: null,
    last_inspection_at: null,
    state_location_lat: null,
    state_location_lng: null,
    active_travel_time_entry_id: null,
    active_work_time_entry_id: null,
    travel_start_odometer: null,
    travel_end_odometer: null,
    accumulated_work_seconds: 0,
    metadata: {},
    active_time_entries: [],
    todays_inspection: null,
    vehicle_inspection: null,
    inspection_overridden: false,
  };
}

function calculateElapsedSeconds(startedAt?: string | null, endedAt?: string | null): number {
  if (!startedAt || !endedAt) return 0;
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.max(0, Math.floor((end - start) / 1000));
}

function resolveActionWorkItemId(
  currentState: WorkflowStateRecord,
  action: WorkflowAction,
  workItemId?: string | null,
): string | null {
  if (workItemId) return String(workItemId);
  if (action === 'start_travel') return currentState.traveling_to_work_item_id || currentState.active_work_item_id || null;
  return currentState.active_work_item_id || currentState.traveling_to_work_item_id || null;
}

export function applyOptimisticWorkflowSelection(params: {
  currentState: WorkflowStateRecord | null;
  clientId: number;
  userId: number;
  nextState: WorkflowState;
  gpsLocation?: GPSLocation | null;
  at?: string;
}): WorkflowStateRecord {
  const { currentState, clientId, userId, nextState, gpsLocation, at } = params;
  const timestamp = at || new Date().toISOString();
  const base = currentState
    ? { ...currentState, metadata: { ...(currentState.metadata || {}) } }
    : createEmptyWorkflowStateRecord(clientId, userId);

  return {
    ...base,
    client_id: clientId,
    user_id: userId,
    current_state: nextState,
    state_started_at: timestamp,
    state_location_lat: gpsLocation?.latitude ?? base.state_location_lat ?? null,
    state_location_lng: gpsLocation?.longitude ?? base.state_location_lng ?? null,
    active_work_item_id: nextState === 'idle' ? null : base.active_work_item_id,
    traveling_to_work_item_id: nextState === 'idle' ? null : base.traveling_to_work_item_id,
    metadata: {
      ...(base.metadata || {}),
      last_offline_selection_at: timestamp,
    },
  };
}

export function applyOptimisticWorkflowReset(params: {
  currentState: WorkflowStateRecord | null;
  clientId: number;
  userId: number;
  gpsLocation?: GPSLocation | null;
  reason?: string | null;
  workItemId?: string | null;
  at?: string;
}): WorkflowStateRecord {
  const { currentState, clientId, userId, gpsLocation, reason, workItemId, at } = params;
  const timestamp = at || new Date().toISOString();
  const base = currentState
    ? { ...currentState, metadata: { ...(currentState.metadata || {}) } }
    : createEmptyWorkflowStateRecord(clientId, userId);

  return {
    ...base,
    client_id: clientId,
    user_id: userId,
    current_state: 'idle',
    active_work_item_id: null,
    traveling_to_work_item_id: null,
    state_started_at: timestamp,
    travel_started_at: null,
    travel_ended_at: null,
    work_started_at: null,
    state_location_lat: gpsLocation?.latitude ?? base.state_location_lat ?? null,
    state_location_lng: gpsLocation?.longitude ?? base.state_location_lng ?? null,
    active_travel_time_entry_id: null,
    active_work_time_entry_id: null,
    travel_start_odometer: null,
    travel_end_odometer: null,
    accumulated_work_seconds: 0,
    active_time_entries: [],
    metadata: {
      ...(base.metadata || {}),
      accumulated_travel_seconds: 0,
      last_offline_action: 'reset_workflow',
      last_offline_action_at: timestamp,
      last_offline_reset_reason: reason || null,
      last_offline_reset_work_item_id: workItemId ? String(workItemId) : null,
    },
  };
}

export function applyOptimisticVehicleWorkflowAction(params: {
  currentState: WorkflowStateRecord | null;
  clientId: number;
  userId: number;
  action: SmartTaskVehicleAction;
  payload: Record<string, unknown>;
  at?: string;
}): WorkflowStateRecord {
  const { currentState, clientId, userId, action, payload, at } = params;
  const timestamp = at || new Date().toISOString();
  const base = currentState
    ? { ...currentState, metadata: { ...(currentState.metadata || {}) } }
    : createEmptyWorkflowStateRecord(clientId, userId);
  const metadata = payload?.metadata && typeof payload.metadata === 'object' && !Array.isArray(payload.metadata)
    ? payload.metadata as Record<string, unknown>
    : {};
  const nextState: WorkflowStateRecord = {
    ...base,
    client_id: clientId,
    user_id: userId,
    metadata: {
      ...(base.metadata || {}),
      ...metadata,
      last_offline_action: action,
      last_offline_action_at: timestamp,
    },
  };

  switch (action) {
    case 'set_vehicle':
      nextState.current_vehicle_id = (payload?.vehicle_id as string | null) ?? base.current_vehicle_id ?? null;
      nextState.current_vehicle_registration = (payload?.vehicle_registration as string | null) ?? base.current_vehicle_registration ?? null;
      break;

    case 'release_vehicle': {
      const releaseVehicleId = (payload?.vehicle_id as string | null) ?? base.current_vehicle_id ?? null;
      nextState.current_vehicle_id = null;
      nextState.current_vehicle_registration = null;
      nextState.current_state = 'idle';
      nextState.state_started_at = timestamp;
      nextState.metadata = {
        ...nextState.metadata,
        last_vehicle_released: releaseVehicleId,
      };
      break;
    }

    case 'record_inspection': {
      const inspectionRecordId = `offline-inspection:${timestamp}`;
      nextState.last_inspection_at = timestamp;
      nextState.inspection_overridden = false;
      nextState.todays_inspection = {
        id: inspectionRecordId,
        created_at: timestamp,
        metadata,
      };
      nextState.vehicle_inspection = {
        id: inspectionRecordId,
        created_at: timestamp,
        metadata,
      };
      if (base.current_state === 'inspection_pending') {
        nextState.current_state = 'ready';
        nextState.state_started_at = timestamp;
      }
      break;
    }

    case 'cancel_inspection': {
      const cancelVehicleId = base.current_vehicle_id;
      nextState.current_state = 'idle';
      nextState.current_vehicle_id = null;
      nextState.current_vehicle_registration = null;
      nextState.inspection_overridden = false;
      nextState.state_started_at = timestamp;
      nextState.metadata = {
        ...nextState.metadata,
        last_vehicle_released: cancelVehicleId,
      };
      break;
    }

    case 'override_inspection':
      nextState.inspection_overridden = true;
      break;

    case 'consume_inspection_override':
      nextState.inspection_overridden = false;
      nextState.metadata = {
        ...nextState.metadata,
        inspection_override_consumed_at: timestamp,
      };
      // Mirror backend: transition from inspection_pending to ready
      if (nextState.current_state === 'inspection_pending') {
        nextState.current_state = 'ready';
        nextState.state_started_at = timestamp;
        nextState.last_inspection_at = timestamp;
      }
      // Ensure todays_inspection is set so hasInspectionToday returns true
      const consumedInspectionRecord = {
        id: `override-consumed:${timestamp}`,
        created_at: timestamp,
        metadata: {
          vehicle_id: nextState.current_vehicle_id,
          vehicle_registration: nextState.current_vehicle_registration,
          inspection_type: 'admin_override',
          result: 'pass',
          admin_override: true,
          override_consumed: true,
          override_consumed_at: timestamp,
        },
      };
      if (nextState.todays_inspection?.metadata?.admin_override) {
        nextState.todays_inspection = {
          ...nextState.todays_inspection,
          metadata: {
            ...(nextState.todays_inspection.metadata || {}),
            override_consumed_at: timestamp,
            override_consumed: true,
          },
        };
      } else if (!nextState.todays_inspection) {
        nextState.todays_inspection = consumedInspectionRecord;
      }
      if (nextState.vehicle_inspection?.metadata?.admin_override) {
        nextState.vehicle_inspection = {
          ...nextState.vehicle_inspection,
          metadata: {
            ...(nextState.vehicle_inspection.metadata || {}),
            override_consumed_at: timestamp,
            override_consumed: true,
          },
        };
      } else if (!nextState.vehicle_inspection) {
        nextState.vehicle_inspection = nextState.todays_inspection;
      }
      break;

    default:
      break;
  }

  return nextState;
}

export function applyOptimisticWorkflowTransition(params: {
  currentState: WorkflowStateRecord | null;
  clientId: number;
  userId: number;
  action: WorkflowAction;
  nextState: WorkflowState;
  workItemId?: string | null;
  payload?: Record<string, unknown>;
  gpsLocation?: GPSLocation | null;
  at?: string;
}): WorkflowStateRecord {
  const { currentState, clientId, userId, action, nextState, workItemId, payload, gpsLocation, at } = params;
  const timestamp = at || new Date().toISOString();
  const base = currentState
    ? { ...currentState, metadata: { ...(currentState.metadata || {}) } }
    : createEmptyWorkflowStateRecord(clientId, userId);
  const effectiveWorkItemId = resolveActionWorkItemId(base, action, workItemId);

  const nextStateRecord: WorkflowStateRecord = {
    ...base,
    client_id: clientId,
    user_id: userId,
    current_state: nextState,
    state_started_at: timestamp,
    state_location_lat: gpsLocation?.latitude ?? base.state_location_lat ?? null,
    state_location_lng: gpsLocation?.longitude ?? base.state_location_lng ?? null,
    metadata: {
      ...(base.metadata || {}),
      last_offline_action: action,
      last_offline_action_at: timestamp,
    },
  };

  switch (action) {
    case 'start_travel':
      nextStateRecord.traveling_to_work_item_id = effectiveWorkItemId;
      nextStateRecord.travel_started_at = timestamp;
      nextStateRecord.travel_ended_at = null;
      nextStateRecord.active_travel_time_entry_id = null;
      nextStateRecord.travel_start_odometer = typeof payload?.odometer === 'number'
        ? payload.odometer
        : base.travel_start_odometer;
      nextStateRecord.metadata = {
        ...nextStateRecord.metadata,
        accumulated_travel_seconds: 0,
      };
      break;

    case 'arrive_site':
      nextStateRecord.active_work_item_id = effectiveWorkItemId;
      nextStateRecord.traveling_to_work_item_id = null;
      nextStateRecord.travel_ended_at = timestamp;
      nextStateRecord.active_travel_time_entry_id = null;
      if (typeof payload?.odometer === 'number') {
        nextStateRecord.travel_end_odometer = payload.odometer;
      }
      break;

    case 'check_in':
    case 'check_in_without_travel':
      nextStateRecord.active_work_item_id = effectiveWorkItemId;
      nextStateRecord.traveling_to_work_item_id = null;
      if (!nextStateRecord.travel_ended_at && base.current_state === 'traveling') {
        nextStateRecord.travel_ended_at = timestamp;
      }
      nextStateRecord.work_started_at = timestamp;
      nextStateRecord.active_work_time_entry_id = null;
      nextStateRecord.accumulated_work_seconds = 0;
      break;

    case 'pause_work': {
      const elapsed = calculateElapsedSeconds(base.work_started_at, timestamp);
      nextStateRecord.accumulated_work_seconds = (base.accumulated_work_seconds || 0) + elapsed;
      nextStateRecord.active_work_time_entry_id = null;
      break;
    }

    case 'resume_work':
      nextStateRecord.active_work_item_id = effectiveWorkItemId || base.active_work_item_id;
      nextStateRecord.work_started_at = timestamp;
      nextStateRecord.active_work_time_entry_id = null;
      break;

    case 'start_completing':
      nextStateRecord.active_work_item_id = effectiveWorkItemId || base.active_work_item_id;
      break;

    case 'complete_job':
      nextStateRecord.active_work_item_id = null;
      nextStateRecord.traveling_to_work_item_id = null;
      nextStateRecord.travel_started_at = null;
      nextStateRecord.travel_ended_at = null;
      nextStateRecord.work_started_at = null;
      nextStateRecord.active_travel_time_entry_id = null;
      nextStateRecord.active_work_time_entry_id = null;
      nextStateRecord.accumulated_work_seconds = 0;
      nextStateRecord.metadata = {
        ...nextStateRecord.metadata,
        accumulated_travel_seconds: 0,
      };
      break;

    default:
      break;
  }

  return nextStateRecord;
}

export function getOptimisticWorkItemPatch(params: {
  action: WorkflowAction;
  workItem?: WorkItem | null;
  payload?: Record<string, unknown>;
  at?: string;
}): Partial<WorkItem> | null {
  const { action, workItem, payload, at } = params;
  if (!workItem) return null;

  const timestamp = at || new Date().toISOString();
  const isAppointment = workItem.item_type === 'appointment';
  const resolution = payload?.resolution || 'completed';

  switch (action) {
    case 'start_travel':
      return {
        status: isAppointment ? 'on_route' : 'en_route',
      };

    case 'arrive_site':
      return isAppointment
        ? { status: 'on_site' }
        : null;

    case 'check_in':
    case 'check_in_without_travel':
      return {
        status: 'in_progress',
        started_at: workItem.started_at || timestamp,
      };

    case 'pause_work':
      return {
        status: 'paused',
      };

    case 'resume_work':
      return {
        status: 'in_progress',
      };

    case 'complete_job': {
      const status = isAppointment
        ? (resolution === 'resolved' ? 'resolved' : 'unresolved')
        : resolution === 'cancelled'
          ? 'cancelled'
          : resolution === 'rescheduled'
            ? 'scheduled'
            : 'resolved';

      return {
        status,
        completed_at: isAppointment || resolution !== 'rescheduled' ? timestamp : workItem.completed_at,
      };
    }

    default:
      return null;
  }
}

export async function saveCachedSmartTaskWorkflowState(params: {
  clientId: number;
  userId: number;
  state: WorkflowStateRecord;
}): Promise<void> {
  const { clientId, userId, state } = params;
  if (!hasValidIdentity(clientId, userId)) return;
  await appShellStore.upsertJson(clientId, getWorkflowSnapshotKey(userId), {
    ...(state as unknown as Record<string, unknown>),
    client_id: clientId,
    user_id: userId,
  });
}

/**
 * Internal storage accessor. App/UI consumers should use the `masterSync`
 * wrappers instead of importing this directly.
 */
export async function loadCachedSmartTaskWorkflowState(clientId: number, userId: number): Promise<WorkflowStateRecord | null> {
  if (!hasValidIdentity(clientId, userId)) return null;
  const row = await appShellStore.getJson<Record<string, unknown>>(clientId, getWorkflowSnapshotKey(userId));
  return row ? (row as unknown as WorkflowStateRecord) : null;
}

export function isQueuedTransitionAlreadySatisfied(
  currentWorkflowState: WorkflowState | null | undefined,
  queuedAction: SmartTaskQueuedWorkflowTransition,
): boolean {
  if (!currentWorkflowState) return false;

  switch (queuedAction.action) {
    case 'start_travel':
      return ['traveling', 'arrived', 'checked_in', 'paused', 'completing'].includes(currentWorkflowState);
    case 'arrive_site':
      return ['arrived', 'checked_in', 'paused', 'completing'].includes(currentWorkflowState);
    case 'check_in':
    case 'check_in_without_travel':
      return ['checked_in', 'paused', 'completing'].includes(currentWorkflowState);
    case 'pause_work':
      return ['paused', 'completing'].includes(currentWorkflowState);
    case 'resume_work':
      return ['checked_in', 'completing'].includes(currentWorkflowState);
    case 'start_completing':
      return ['completing', 'idle', 'ready'].includes(currentWorkflowState);
    case 'complete_job':
      return ['idle', 'ready'].includes(currentWorkflowState);
    default:
      return currentWorkflowState === queuedAction.to_state;
  }
}

export function isQueuedWorkflowResetAlreadySatisfied(
  currentWorkflowState: WorkflowStateRecord | null | undefined,
  queuedAction: SmartTaskQueuedWorkflowReset,
): boolean {
  if (!currentWorkflowState) return false;

  const currentState = currentWorkflowState.current_state;
  if (currentState !== 'idle' && currentState !== 'ready') {
    return false;
  }

  if (!queuedAction.work_item_id) {
    return true;
  }

  return String(currentWorkflowState.active_work_item_id || '') !== queuedAction.work_item_id
    && String(currentWorkflowState.traveling_to_work_item_id || '') !== queuedAction.work_item_id;
}

async function readCurrentWorkflowState(token: string): Promise<WorkflowStateRecord | null> {
  try {
    const response = await fetch(getApiUrl('/api/v1/work-items/workflow-state'), withOperationsDemoFetchInit({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }));

    if (!response.ok) return null;
    const data = await response.json().catch(() => null);
    if (!data?.success) return null;
    return data.data || null;
  } catch {
    return null;
  }
}

function truncateForResponsePayload(value: unknown): string | undefined {
  try {
    const serialized = JSON.stringify(value ?? null);
    return serialized.length > 65536 ? serialized.slice(0, 65536) : serialized;
  } catch {
    return undefined;
  }
}

let smartTaskSyncInFlight: Promise<{ synced: number; failed: number; remaining: number }> | null = null;

export async function syncPendingSmartTaskActions(params: {
  clientId: number;
  userId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  if (smartTaskSyncInFlight) {
    return smartTaskSyncInFlight;
  }

  const inFlight = runPendingSmartTaskActionSync(params)
    .finally(() => {
      smartTaskSyncInFlight = null;
    });
  smartTaskSyncInFlight = inFlight;
  return inFlight;
}

async function runPendingSmartTaskActionSync(params: {
  clientId: number;
  userId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const { clientId, userId, token, onProgress } = params;
  if (!hasValidIdentity(clientId, userId) || !token || !offlineDb) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const rows = await loadPendingQueueRows(clientId, userId);
  if (rows.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  let synced = 0;
  let failed = 0;
  const syncStartedAt = new Date().toISOString();

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const entry = publishRowToEntry(row);

    if (!entry || row.id == null) {
      if (row.id != null) {
        await offlineDb.publishQueue.update(row.id, {
          status: 'dead_letter',
          error_message: 'Unparseable queued payload',
          updated_at: new Date().toISOString(),
        }).catch(() => undefined);
      }
      continue;
    }

    // Honour the retry backoff recorded by previous failed attempts.
    if (row.status === 'failed' && row.next_retry_at && row.next_retry_at > syncStartedAt) {
      continue;
    }

    onProgress?.({
      current: index + 1,
      total: rows.length,
      message: `Syncing ${entry.timeline_label}...`,
    });

    const rowId = row.id;
    const attemptNumber = row.attempts + 1;
    await offlineDb.publishQueue.update(rowId, {
      status: 'processing',
      attempts: attemptNumber,
      last_attempt: syncStartedAt,
      updated_at: syncStartedAt,
    }).catch(() => undefined);

    const markCompleted = async (responseData?: unknown) => {
      synced += 1;
      await offlineDb!.publishQueue.update(rowId, {
        status: 'completed',
        updated_at: new Date().toISOString(),
        response_payload: truncateForResponsePayload(responseData),
      }).catch(() => undefined);
      await upsertActionHistoryEntry(clientId, userId, {
        local_action_id: entry.local_action_id,
        offline_action_id: entry.offline_action_id,
        kind: entry.kind,
        action: entry.action,
        status: 'synced',
        timeline_label: entry.timeline_label,
        work_item_id: entry.work_item_id,
        created_at: entry.created_at,
        synced_at: new Date().toISOString(),
        activity_context: entry.activity_context || null,
      });
      await updateOfflineMutation({
        clientId,
        dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
        status: 'synced',
        syncedAt: new Date().toISOString(),
        lastError: null,
        attempts: attemptNumber,
      });
    };

    try {
      let responseData: {
        success?: boolean;
        error?: string;
        data?: { state?: WorkflowStateRecord } & Record<string, unknown>;
      } | null = null;

      if (entry.kind === 'workflow_transition') {
        const response = await fetch(getApiUrl('/api/v1/work-items/workflow-state'), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || !responseData?.success) {
          const currentState = await readCurrentWorkflowState(token);
          if (isQueuedTransitionAlreadySatisfied(currentState?.current_state, entry)) {
            if (currentState) {
              await saveCachedSmartTaskWorkflowState({ clientId, userId, state: currentState });
            }
            await markCompleted();
            continue;
          }

          throw new Error(responseData?.error || `Workflow replay failed (${response.status})`);
        }

        if (responseData?.data?.state) {
          await saveCachedSmartTaskWorkflowState({ clientId, userId, state: responseData.data.state });
        }
      } else if (entry.kind === 'workflow_reset') {
        const response = await fetch(getApiUrl('/api/v1/work-items/workflow-state'), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || !responseData?.success) {
          const currentState = await readCurrentWorkflowState(token);
          if (isQueuedWorkflowResetAlreadySatisfied(currentState, entry)) {
            if (currentState) {
              await saveCachedSmartTaskWorkflowState({ clientId, userId, state: currentState });
            }
            await markCompleted();
            continue;
          }

          throw new Error(responseData?.error || `Workflow reset replay failed (${response.status})`);
        }

        if (responseData?.data?.state) {
          await saveCachedSmartTaskWorkflowState({ clientId, userId, state: responseData.data.state });
        }
      } else if (entry.kind === 'work_item_status_update') {
        const response = await fetch(getApiUrl(`/api/v1/work-items/${entry.work_item_id}`), withOperationsDemoFetchInit({
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || responseData?.success === false) {
          throw new Error(responseData?.error || `Work item status replay failed (${response.status})`);
        }
      } else if (entry.kind === 'vehicle_action') {
        const vehicleActionPayload = entry.action === 'record_inspection'
          ? stripVehicleInspectionOfflineSource(
              await enrichVehicleInspectionActionPayload({
                payload: entry.payload as VehicleInspectionActionPayload,
                token,
              }),
            )
          : entry.payload;

        const response = await fetch(getApiUrl('/api/v1/work-items/workflow-state'), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: entry.action, ...vehicleActionPayload }),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || responseData?.success === false) {
          throw new Error(responseData?.error || `Vehicle action replay failed (${response.status})`);
        }

        if (responseData?.data?.state) {
          await saveCachedSmartTaskWorkflowState({ clientId, userId, state: responseData.data.state });
        }
      } else if (entry.kind === 'fleet_action') {
        const response = await fetch(getApiUrl('/api/v1/work-items/fleet'), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || responseData?.success === false) {
          throw new Error(responseData?.error || `Fleet action replay failed (${response.status})`);
        }
      } else if (entry.kind === 'field_service_action') {
        const response = await fetch(getApiUrl(entry.endpoint), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || responseData?.success === false) {
          throw new Error(responseData?.error || `Field service action replay failed (${response.status})`);
        }
      } else if (entry.kind === 'logbook_action') {
        const response = await fetch(getApiUrl(entry.endpoint), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || responseData?.success === false) {
          throw new Error(responseData?.error || `Logbook replay failed (${response.status})`);
        }
      } else if (entry.kind === 'comment_create') {
        const response = await fetch(getApiUrl(entry.endpoint), withOperationsDemoFetchInit({
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        responseData = await response.json().catch(() => null);
        if (!response.ok || responseData?.success === false) {
          throw new Error(responseData?.error || `Comment replay failed (${response.status})`);
        }
      } else {
        const response = await fetch(getApiUrl('/api/v1/routes'), withOperationsDemoFetchInit({
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
        }));

        if (!response.ok) {
          responseData = await response.json().catch(() => null);
          throw new Error(responseData?.error || `Route stop replay failed (${response.status})`);
        }
      }

      await markCompleted(responseData);
    } catch (error: unknown) {
      failed += 1;
      const isDeadLetter = attemptNumber >= MAX_SYNC_ATTEMPTS;
      const lastError = error instanceof Error ? error.message : 'SmartTask offline action sync failed';

      await offlineDb.publishQueue.update(rowId, {
        status: isDeadLetter ? 'dead_letter' : 'failed',
        updated_at: new Date().toISOString(),
        error_message: lastError,
        next_retry_at: isDeadLetter ? undefined : computeSmartTaskRetryBackoff(attemptNumber + 1),
      }).catch(() => undefined);

      if (isDeadLetter) {
        await upsertActionHistoryEntry(clientId, userId, {
          local_action_id: entry.local_action_id,
          offline_action_id: entry.offline_action_id,
          kind: entry.kind,
          action: entry.action,
          status: 'failed',
          timeline_label: entry.timeline_label,
          work_item_id: entry.work_item_id,
          created_at: entry.created_at,
          error: lastError,
          activity_context: entry.activity_context || null,
        });
      }

      await updateOfflineMutation({
        clientId,
        dedupeKey: getSmartTaskMutationDedupeKey(entry.offline_action_id),
        status: isDeadLetter ? 'failed' : 'pending',
        lastError,
        attempts: attemptNumber,
      });
    }
  }

  const remainingRows = await loadPendingQueueRows(clientId, userId);
  return {
    synced,
    failed,
    remaining: remainingRows.length,
  };
}
